import { getApps, initializeApp, type FirebaseOptions } from "firebase/app";
import {
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";

let appCheck: AppCheck | null = null;

type GlobalWithAppCheckDebug = typeof globalThis & {
  FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
};

function getFirebaseApp() {
  try {
    const configJson = process.env.NEXT_PUBLIC_FIREBASE_CONFIG;
    if (!configJson) {
      return null;
    }

    const existingApps = getApps();
    if (existingApps.length > 0) {
      return existingApps[0];
    }

    const config = JSON.parse(configJson) as FirebaseOptions;
    return initializeApp(config);
  } catch (error) {
    console.warn("Failed to initialize Firebase app:", error);
    return null;
  }
}

function getAppCheck(): AppCheck | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (appCheck) {
    return appCheck;
  }

  try {
    const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (!recaptchaSiteKey) {
      return null;
    }

    const app = getFirebaseApp();
    if (!app) {
      return null;
    }

    if (process.env.NODE_ENV === "development") {
      (globalThis as GlobalWithAppCheckDebug).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });

    return appCheck;
  } catch (error) {
    console.warn("Failed to initialize Firebase App Check:", error);
    return null;
  }
}

export async function getAppCheckToken(): Promise<string | null> {
  try {
    const appCheckInstance = getAppCheck();
    if (!appCheckInstance) {
      return null;
    }

    const result = await getToken(appCheckInstance, false);
    return result.token;
  } catch (error) {
    console.warn("Failed to get Firebase App Check token:", error);
    return null;
  }
}
